import React, { useState } from 'react';
import { Send, Paperclip, Users, Clock, CheckCheck, User } from 'lucide-react';

const CommunicationPanel = () => {
  const [message, setMessage] = useState('');
  const [selectedThread, setSelectedThread] = useState(null);

  // Mock conversation threads
  const threads = [
    {
      id: 1,
      title: 'Annual Safety Audit - Plant A',
      participants: ['John Smith', 'Sarah Johnson', 'Mike Wilson'],
      lastMessage: 'Can we schedule the final review for next week?',
      lastMessageTime: '2025-12-06 11:30',
      unreadCount: 2,
      status: 'active'
    },
    {
      id: 2,
      title: 'Environmental Compliance Report',
      participants: ['Sarah Johnson', 'Emily Davis'],
      lastMessage: 'All documents have been submitted for approval',
      lastMessageTime: '2025-12-06 10:15',
      unreadCount: 0,
      status: 'resolved'
    },
    {
      id: 3,
      title: 'Fire Safety Certification - Plant B',
      participants: ['Mike Wilson', 'John Smith', 'Tom Brown'],
      lastMessage: 'Training session scheduled for December 15th',
      lastMessageTime: '2025-12-05 16:45',
      unreadCount: 1,
      status: 'active'
    }
  ];

  // Mock messages for selected thread
  const messages = [
    {
      id: 1,
      sender: 'John Smith',
      content: 'Has everyone reviewed the latest safety audit findings?',
      timestamp: '2025-12-06 09:00',
      isCurrentUser: false
    },
    {
      id: 2,
      sender: 'You',
      content: 'Yes, I have reviewed them. We need to address the equipment maintenance issues.',
      timestamp: '2025-12-06 09:15',
      isCurrentUser: true
    },
    {
      id: 3,
      sender: 'Sarah Johnson',
      content: 'I agree. I can coordinate with the maintenance team to schedule the necessary work.',
      timestamp: '2025-12-06 09:30',
      isCurrentUser: false
    },
    {
      id: 4,
      sender: 'Mike Wilson',
      content: 'Great! Can we schedule the final review for next week?',
      timestamp: '2025-12-06 11:30',
      isCurrentUser: false
    }
  ];

  const handleSendMessage = () => {
    if (message?.trim()) {
      // Send message logic here
      setMessage('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'resolved':
        return 'bg-blue-500';
      case 'urgent':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Thread List */}
      <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Conversations</h2>
          <p className="text-sm text-gray-600 mt-1">Compliance team discussions</p>
        </div>

        <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
          {threads?.map((thread) => (
            <div
              key={thread?.id}
              onClick={() => setSelectedThread(thread)}
              className={`p-4 cursor-pointer transition-colors ${
                selectedThread?.id === thread?.id
                  ? 'bg-blue-50 border-l-4 border-blue-600' :'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-medium text-gray-900 text-sm line-clamp-1">
                  {thread?.title}
                </h3>
                {thread?.unreadCount > 0 && (
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">
                    {thread?.unreadCount}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-600">
                  {thread?.participants?.length} participants
                </span>
                <div className={`ml-auto w-2 h-2 rounded-full ${getStatusColor(thread?.status)}`} />
              </div>

              <p className="text-sm text-gray-600 line-clamp-1 mb-1">
                {thread?.lastMessage}
              </p>

              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                {new Date(thread.lastMessageTime)?.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-gray-200">
          <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            New Conversation
          </button>
        </div>
      </div>
      {/* Message View */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        {selectedThread ? (
          <>
            {/* Thread Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{selectedThread?.title}</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {selectedThread?.participants?.join(', ')}
                    </span>
                  </div>
                </div>
                <span className={`inline-block px-3 py-1 rounded-lg text-xs font-medium text-white ${getStatusColor(selectedThread?.status)}`}>
                  {selectedThread?.status}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 space-y-4 overflow-y-auto max-h-[400px]">
              {messages?.map((msg) => (
                <div
                  key={msg?.id}
                  className={`flex gap-3 ${msg?.isCurrentUser ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    msg?.isCurrentUser ? 'bg-blue-600' : 'bg-gray-300'
                  }`}>
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className={`flex-1 max-w-[70%] ${msg?.isCurrentUser ? 'items-end' : ''}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-900">{msg?.sender}</span>
                      <span className="text-xs text-gray-500">{msg?.timestamp}</span>
                    </div>
                    <div
                      className={`p-3 rounded-lg ${
                        msg?.isCurrentUser
                          ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm">{msg?.content}</p>
                    </div>
                    {msg?.isCurrentUser && (
                      <div className="flex items-center gap-1 mt-1 justify-end">
                        <CheckCheck className="w-4 h-4 text-blue-600" />
                        <span className="text-xs text-gray-500">Read</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex gap-2">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <Paperclip className="w-5 h-5 text-gray-600" />
                </button>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendMessage}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Select a conversation to view messages</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunicationPanel;